<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToPage'
});
</script>
<template>
	<div v-if="cShowInSingle" :key="langKey" v-align="align" :flex="flex ? flex : null" :class="setClass" class="to-page">
		<template v-if="type === 'lite'">
			<div class="to-page-first"></div>
			<div class="to-page-prev">{{ t('to.page.prevPage') }}</div>
			<div class="to-page-stat">{{ currentPage_ }} / {{ total }}</div>
			<div class="to-page-next">{{ t('to.page.nextPage') }}</div>
			<div class="to-page-last"></div>
		</template>
		<template v-else>
			<div v-if="layout.includes('total') || layout === 'all'" class="to-page-total">{{ t('to.page.total', total) }}</div>
			<div class="to-page-slot">
				<slot />
			</div>
			<div v-if="layout.includes('pager') || layout === 'all'" class="to-page-pager">
				<div v-dis="Number(currentPage_) <= 1" class="to-page-pager-prev" @click="handlePagePrev"></div>
				<div class="to-page-pager-list">
					<div class="to-page-pager-item" :class="{ 'is-on': Number(currentPage_) === 1 }" @click="handleCurrentChange(1)">
						<div class="to-page-pager-item-text">1</div>
					</div>
					<div v-if="showPrevMore" class="to-page-pager-ellipsis">…</div>
					<div v-for="page in pager_" :key="page" class="to-page-pager-item" :class="{ 'is-on': Number(currentPage_) === page }" @click="handleCurrentChange(page)">
						<div class="to-page-pager-item-text">{{ page }}</div>
					</div>
					<div v-if="showNextMore" class="to-page-pager-ellipsis">…</div>
					<div v-if="pageCount_ > 1" class="to-page-pager-item" :class="{ 'is-on': Number(currentPage_) === Number(pageCount_) }" @click="handleCurrentChange(pageCount_)">
						<div class="to-page-pager-item-text">{{ pageCount_ }}</div>
					</div>
				</div>
				<div v-dis="Number(currentPage_) >= Number(pageCount_)" class="to-page-pager-next" @click="handlePageNext"></div>
			</div>
			<div v-if="layout.includes('size') || layout === 'all'" class="to-page-size">
				<to-select v-model="pageSize_" v-dis="disabled" class="to-page-size-select" :unit="t('to.page.pageSize', '')" :clearable="false" :data="cPageSizes" @change="handleSizeChange" />
			</div>
			<div v-if="layout.includes('jumper') || layout === 'all'" class="to-page-jumper">
				<to-input v-model="jumperPage" v-dis="disabled" :clearable="false" type="number" class="to-page-jumper-input" :placeholder="t('to.page.go')" @change="handleJump">
					<to-icon value="next" class="to-page-jumper-icon" link></to-icon>
				</to-input>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, nextTick } from 'vue'
import { ToInput, ToSelect } from '@touchvue/components'
import { vDis } from '@touchvue/directives'
import { t, langKey } from '@touchvue/locale'


// const slots = useSlots
const props = defineProps({
	flex: Boolean,
	type: { type: String, default: '' },
	mode: { type: String, default: 'default' },
	align: { type: String, default: '' },
	pageSize: { type: [Number, String], default: 10 },
	total: { type: [Number, String], default: 0 },
	pageCount: { type: [Number, String], default: undefined },
	pagerCount: { type: [Number, String], default: 7 },
	modelValue: { type: [Number, String], default: 1 },
	layout: { type: String, default: 'all' },
	pageSizes: { type: [Array, String], default: () => [10, 20, 30, 40, 50, 100] },
	prevText: { type: String, default: '' },
	nextText: { type: String, default: '' },
	disabled: { type: Boolean, default: false },
	autoHide: { type: Boolean, default: false }
})

const emit = defineEmits(['update:pageSize', 'size-change', 'current-change', 'prev-click', 'next-click', 'update:modelValue', 'change'])

const pageCount_ = ref<number>(0)
const pageSize_ = ref<number>(Number(props.pageSize))
const currentPage_ = ref<number>(Number(props.modelValue))
const pager_ = ref<number[]>([])
const jumperPage = ref<string>('')
const showPrevMore = ref(false)
const showNextMore = ref(false)

const cShowInSingle = computed(() => {
	return !(props.autoHide && Number(pageCount_.value) <= 1) || !props.autoHide
})

const cPageSizes = computed(() => {
	const sizes = typeof props.pageSizes === 'string' ? props.pageSizes.split(',') : props.pageSizes
	return sizes.map(size => ({
		label: String(size),
		value: String(size)
	}))
})

const setClass = computed(() => {
	let arr: string[] = []
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	return arr
})

watch(
	() => props.pageSize,
	val => {
		const sizes = typeof props.pageSizes === 'string' ? props.pageSizes.split(',') : props.pageSizes
		if (props.layout.includes('size') || props.layout === 'all') {
			pageSize_.value = sizes.includes(Number(val)) ? Number(val) : Number(sizes[0])
		} else {
			pageSize_.value = Number(val)
		}
		nextTick(() => {
			computePager()
		})
	},
	{ immediate: true }
)

watch(
	() => currentPage_.value,
	val => {
		emit('update:modelValue', Number(val))
	}
)

watch(
	() => props.modelValue,
	val => {
		currentPage_.value = Number(val)
		computePager()
	}
)

watch(
	() => props.pageCount,
	val => {
		pageCount_.value = Number(val)
		computePager()
	},
	{ immediate: true }
)

watch(() => props.total, computePager)

watch(() => props.pagerCount, computePager)

onMounted(() => {
	computePager()
})

function handleJump() {
	if (props.disabled || !jumperPage.value) return
	let jump = Number(jumperPage.value)
	jump = Math.max(1, Math.min(jump, pageCount_.value))
	jumperPage.value = ''
	handleCurrentChange(jump)
}

function handleSizeChange(size: number) {
	if (props.disabled) return
	currentPage_.value = 1
	emit('update:pageSize', Number(size))
	emit('size-change', Number(size))
	emit('current-change', 1)
	emit('change', 1, Number(size))
	nextTick(() => {
		computePager()
	})
}

function handleCurrentChange(page: number) {
	if (props.disabled) return
	currentPage_.value = page
	emit('current-change', Number(page))
	emit('change', Number(page), Number(pageSize_.value))
	nextTick(() => {
		computePager()
	})
}

function handlePagePrev() {
	if (props.disabled || currentPage_.value <= 1) return
	currentPage_.value--
	handleCurrentChange(currentPage_.value)
	emit('prev-click', currentPage_.value)
}

function handlePageNext() {
	if (props.disabled || currentPage_.value >= pageCount_.value) return
	currentPage_.value++
	handleCurrentChange(currentPage_.value)
	emit('next-click', currentPage_.value)
}

function computePager() {
	const pagerCount = Number(props.pagerCount)
	const halfPagerCount = (pagerCount - 1) / 2
	const count = props.pageCount ? Number(props.pageCount) : Math.ceil(Number(props.total) / pageSize_.value)
	pageCount_.value = Math.max(1, count)

	// 当 total 变化导致 pageCount 缩小时，将当前页码钳制到有效范围
	if (currentPage_.value > pageCount_.value) {
		currentPage_.value = pageCount_.value
	}

	const current = currentPage_.value
	// 与 showNextMore 一致：总页数不超过 pagerCount 时用「全展开」布局，避免多余省略号及与左侧固定「1」重复
	showPrevMore.value = current > pagerCount - halfPagerCount && pageCount_.value > pagerCount
	showNextMore.value = current < pageCount_.value - halfPagerCount && pageCount_.value > pagerCount

	const pages: number[] = []

	if (showPrevMore.value && !showNextMore.value) {
		// 左侧已单独渲染第 1 页，中间列表从 2 起，避免出现两个「1」
		const start = Math.max(2, pageCount_.value - (pagerCount - 2))
		for (let i = start; i < pageCount_.value; i++) pages.push(i)
	} else if (!showPrevMore.value && showNextMore.value) {
		for (let i = 2; i < pagerCount; i++) pages.push(i)
	} else if (showPrevMore.value && showNextMore.value) {
		const offset = Math.floor(pagerCount / 2) - 1
		for (let i = current - offset; i <= current + offset; i++) pages.push(i)
	} else {
		for (let i = 2; i < pageCount_.value; i++) pages.push(i)
	}

	pager_.value = pages
}
</script>
